import { Box } from "@material-ui/core";
import React, { useContext, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import { B2BLoadingPopup, useDeviceTypes } from "halifax";
import { CorpSessionInfo, FlightShopStep } from "redmond";
import {
  CDN_ASSETS,
  getConfigTenant,
  getCurrentCorpAccount,
  isCorpTenant,
  useQueryParamState,
} from "@capone/common";
import { PackagesFlightShopConnectorProps } from "./container";
import { DesktopFlightShop } from "./components/DesktopFlightShop";
import { MobileFlightShop } from "./components/MobileFlightShop";
import {
  CORPORATE_SEARCHING_FOR_FLIGHTS_SECONDARY_MESSAGE,
  EARN_ENHANCEMENT_SUBTITLE,
  SEARCHING_FOR_FLIGHTS_SECONDARY_MESSAGE,
} from "./textConstants";
import { SEARCHING_FOR_FLIGHTS } from "./components/FlightList/components/FlightListInfo/textConstants";
import { PackagesAvailabilityCallState } from "../availability/reducer/state";
import { goToHotelShop } from "../hotel-shop/utils/queryStringHelpers";
import { config } from "../../api/config";
import { ClientContext } from "../../App";
import { useExperiments, XMOD_V1_EXPERIMENT } from "../../context/experiments";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";

export interface IPackagesFlightShopProps
  extends PackagesFlightShopConnectorProps,
    RouteComponentProps {}

export const PackagesFlightShop = (props: IPackagesFlightShopProps) => {
  const {
    largestValueAccount,
    isFlightShopLoading,
    fetchPackagesFlightShop,
    history,
    packagesAvailabilityCallState,
    rewardsAccounts,
    setInternalPackagesFlightShopProgress,
  } = props;
  const { matchesDesktop, matchesMobile } = useDeviceTypes();

  const { sessionInfo } = useContext(ClientContext);

  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const [flightShopProgress, setPackagesFlightShopProgress] =
    useQueryParamState<FlightShopStep>(history, "flightShopProgress");

  useEffect(() => {
    window.scrollTo(0, 0);

    if (flightShopProgress === FlightShopStep.ReviewItinerary) {
      // if we're navigating from itinerary review (via breadcrumbs or browser back button)
      // make sure we're on the return step to prevent buggy flight list behavior
      setPackagesFlightShopProgress(FlightShopStep.ChooseReturn);
    }

    // only fetch flight shop if we're not coming from review/checkout (i.e. navigating back)
    if (
      ![FlightShopStep.ReviewItinerary, FlightShopStep.ChooseReturn].includes(
        flightShopProgress
      )
    ) {
      fetchPackagesFlightShop(history);
    }
  }, []);

  useEffect(() => {
    // If packages availability state is not there, redirect back to packages
    if (
      packagesAvailabilityCallState === PackagesAvailabilityCallState.NotCalled
    ) {
      goToHotelShop({ history });
    }
  }, [packagesAvailabilityCallState]);

  useEffect(() => {
    // sync redux state with url param
    setInternalPackagesFlightShopProgress(flightShopProgress);
  }, [flightShopProgress]);

  const isCorporate = isCorpTenant(getConfigTenant());
  const isPrimaryUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "PR";
  const showEarnEnhancement =
    !!largestValueAccount && !!largestValueAccount.earn.flightsMultiplier;

  const currentAccount = getCurrentCorpAccount(rewardsAccounts, sessionInfo);

  const accountToDisplayOnLoading = isCorpTenant(config.TENANT)
    ? currentAccount || largestValueAccount
    : largestValueAccount;

  return (
    <Box className="packages-flight-shop-root">
      {isFlightShopLoading && (
        <B2BLoadingPopup
          open={isFlightShopLoading}
          message={SEARCHING_FOR_FLIGHTS}
          secondaryMessage={
            isCorporate && (!isPrimaryUser || !showEarnEnhancement)
              ? CORPORATE_SEARCHING_FOR_FLIGHTS_SECONDARY_MESSAGE
              : showEarnEnhancement
              ? EARN_ENHANCEMENT_SUBTITLE(
                  accountToDisplayOnLoading.earn.flightsMultiplier,
                  accountToDisplayOnLoading.productDisplayName,
                  accountToDisplayOnLoading.rewardsBalance
                    .currencyDescription ??
                    accountToDisplayOnLoading.rewardsBalance.currency.toLowerCase()
                )
              : SEARCHING_FOR_FLIGHTS_SECONDARY_MESSAGE
          }
          image={CDN_ASSETS.capone.searchLoading.flights}
          className="flight-list-loading-popup"
          popupSize={matchesMobile ? "mobile" : "desktop"}
          isXmod={isXmodRedesign}
        />
      )}
      {matchesMobile && <MobileFlightShop />}
      {matchesDesktop && <DesktopFlightShop />}
    </Box>
  );
};
